/**
 * BottomNavComponent
 *
 * This component defines the bottom tab navigator used across the app.
 * It includes several screens such as Parking, Attractions, Bathrooms, Events, and Settings.
 * The `Events` tab has a custom floating action button (FAB) with a unique design that
 * differentiates it from the rest of the tabs.
 *
 * The navigator is designed to adjust based on device screen dimensions to provide
 * a responsive UI for both mobile and web platforms.
 *
 * Major Components:
 * 1. ParkingScreen - Displays the content for the Parking tab.
 * 2. AttractionsScreen - Displays the content for the Attractions tab.
 * 3. BathroomsScreen - Displays the content for the Bathrooms tab.
 * 4. EventsScreen - Displays the content for the Events tab, with custom button styling.
 * 5. SettingsScreen - Displays the content for the Settings tab.
 *
 * CustomTabBarButton: This is a custom button used for the `Events` tab that
 * stands out as a floating action button (FAB) above the bottom tab bar.
 * It is larger and has a shadow effect to create a "floating" appearance.
 *
 * ScreenOptions:
 * - Each tab is associated with an icon from FontAwesome.
 * - The `tabBarIcon` function selects the appropriate icon based on the route name.
 * - Active and inactive tab colors are managed using `tabBarActiveTintColor` and `tabBarInactiveTintColor`.
 *
 * Styles:
 * - The bottom tab bar is styled with a rounded top and shadow effect for visual depth.
 * - The `Events` tab button is positioned higher than other tabs, with an elevated, circular design.
 * - The screen content is vertically centered with text size matching across different tabs.
 *
 * Dependencies:
 * - React Navigation for tab-based navigation.
 * - FontAwesomeIcon for tab icons.
 * - COLORS from a constants file to ensure consistent theming across the app.
 *
 * Responsive Design Considerations:
 * - The tab navigator adjusts the padding, height, and other layout features based on the screen size,
 *   ensuring a smooth experience on both web and mobile platforms.
 */
import React, { lazy, Suspense } from "react";
import { createBottomTabNavigator, BottomTabBar } from "@react-navigation/bottom-tabs";
import { View, StyleSheet, TouchableOpacity, Platform } from "react-native";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faSquareParking, faTicket, faRestroom, faCalendarDay } from "@fortawesome/free-solid-svg-icons";
import { useIsFocused } from "@react-navigation/native";
import { COLORS } from "../../constants";
import ParkingStack from "../ParkingStack/ParkingStack";
import AttractionsStack from "../AttractionsStack/AttractionsStack";
import EventsStack from "../EventsStack/EventsStack";
import BathroomStack from "../BathroomStack/BathroomStack";
import EventStore from "../../storage/eventStore";
import Loading from "../Loading/Loading";

const Settings = lazy(() => import("../../views/Settings/Settings"));

const Tab = createBottomTabNavigator();
/**
 * CustomTabBarButton Component
 *
 * A custom tab button used for the center tab (Events).
 *
 * @param {Object} props - The props passed to the component.
 * @param {Function} props.onPress - The press handler function for the button.
 * @param {Object} props.children - The icon or content to be displayed inside the button.
 */
const CustomTabBarButton = ({ children, onPress }) => {
  const isSelected = useIsFocused();
  return (
    <TouchableOpacity
      style={styles.customTabBarButton}
      onPress={onPress}
      aria-label="Events Tab"
      role="tab"
      tabIndex={0}
    >
      <View style={[styles.customButton, isSelected && styles.glowButton]}>{children}</View>
    </TouchableOpacity>
  );
};

const LazyWrapper = ({ children }) =>
  Platform.OS === "web" ? <Suspense fallback={<Loading />}>{children}</Suspense> : children;

/**
 * BottomNavComponent Component
 *
 * Defines the bottom tab navigator with custom styling and screen options for each tab.
 * The central Events tab is customized to have a floating action button (FAB) style.
 */
const BottomNavComponent = () => {
  return (
    <Tab.Navigator
      initialRouteName="Events"
      backBehavior="history"
      screenOptions={({ route }) => ({
        tabBarIcon: ({ color }) => {
          let icon;
          if (route.name === "Events") icon = faCalendarDay;
          else if (route.name === "Parking") icon = faSquareParking;
          else if (route.name === "Attractions") icon = faTicket;
          else if (route.name === "Bathrooms") icon = faRestroom;
          else if (route.name === "Settings") icon = faCog;
          return <FontAwesomeIcon icon={icon} size={"xl"} color={color} />;
        },
        tabBarActiveTintColor: COLORS.secondary,
        tabBarInactiveTintColor: COLORS.border,
        tabBarLabelStyle: { fontSize: 14 },
      })}
      tabBar={(props) => (
        <View>
          <BottomTabBar {...props} style={styles.webTabBar} />
          <View style={{ backgroundColor: COLORS.textLight, height: 10 }} />
        </View>
      )}
    >
      <Tab.Screen
        name="Parking"
        component={ParkingStack}
        options={{ headerShown: false }}
        listeners={({ navigation }) => ({
          tabPress: () => {
            const noParkingRouteHistory =
              navigation.getState().routes.find((route) => route.name === "Parking").state?.routes?.length === 1;

            if (noParkingRouteHistory) {
              window.location.href = `${window.location.origin}/parking`;
            }
          },
        })}
      />
      <Tab.Screen name="Attractions" component={AttractionsStack} options={{ headerShown: false }} />
      <Tab.Screen
        name="Events"
        component={EventsStack}
        options={({ navigation }) => ({
          headerShown: false,
          tabBarButton: (props) => (
            <CustomTabBarButton
              {...props}
              onPress={() => {
                const selectedCategories = EventStore.getState().selectedCategories;
                const params = selectedCategories.length > 0 ? { category: selectedCategories.join("|") } : {};
                navigation.navigate("Events", { screen: "ListView", params });
              }}
            >
              <FontAwesomeIcon icon={faCalendarDay} size={"lg"} color={COLORS.textLight} />
            </CustomTabBarButton>
          ),
        })}
      />
      <Tab.Screen
        name="Bathrooms"
        component={BathroomStack}
        options={{ headerShown: false }}
        listeners={({ navigation }) => ({
          tabPress: () => {
            const noBathroomRouteHistory =
              navigation.getState().routes.find((route) => route.name === "Bathrooms").state?.routes?.length === 1;

            if (noBathroomRouteHistory) {
              window.location.href = `${window.location.origin}/bathrooms`;
            }
          },
        })}
      />
      <Tab.Screen name="Settings" options={{ headerShown: true }}>
        {() => (
          <LazyWrapper>
            <Settings />
          </LazyWrapper>
        )}
      </Tab.Screen>
    </Tab.Navigator>
  );
};

export default BottomNavComponent;
/**
 * Styles for the BottomNavComponent and its children components.
 */
const styles = StyleSheet.create({
  webTabBar: {
    backgroundColor: COLORS.textLight,
    paddingBottom: 10,
    paddingTop: 10,
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    shadowColor: COLORS.textDark,
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.15,
    shadowRadius: 5,
    borderTopColor: COLORS.border,
    borderTopWidth: 0.5,
  },
  customTabBarButton: {
    top: -30,
    justifyContent: "center",
    alignItems: "center",
  },
  customButton: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: COLORS.primary,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: COLORS.textDark,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 5,
    elevation: 10,
  },
  glowButton: {
    backgroundColor: "green",
    borderWidth: 1.5,
    borderColor: "#004d00",
    shadowColor: "green",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 15,
    elevation: 20,
  },
});
